import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import App from "./App";
import "./index.css";
import { initPwaInstallCapture } from "./lib/pwaInstall";

// Capture beforeinstallprompt before any screen mounts; it fires only once.
initPwaInstallCapture();
import { queryClient } from "./lib/queryClient";
// Rudderstack JS singleton — explicit business events → GA4 + hub webhook.
// The Rudderstack PostHog destination is intentionally disconnected for the
// limephone-web source; posthog-js below owns client-side PostHog so we
// don't double-count events.
import "./lib/rudderstack";

// posthog-js for session replay, autocapture, heatmaps, $pageview, feature
// flags. Same project + cross_subdomain_cookie as the marketing site
// (GTM-5TZJZXM on limephone.io) so a visitor's distinct_id stitches from
// first marketing touch through signup to paid. rsAnalytics.identify()
// mirrors into posthog.identify() on login (see ./lib/rudderstack.ts).
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
const posthogHost =
  (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) ??
  "https://us.i.posthog.com";
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    cross_subdomain_cookie: true,
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: false,
    session_recording: { maskAllInputs: true },
    persistence: "localStorage+cookie",
  });
}

// Polyfill Array.at() for older browsers (Huawei Browser 11.x, etc.)
if (!Array.prototype.at) {
  Array.prototype.at = function (index: number) {
    const i = index >= 0 ? index : this.length + index;
    return this[i];
  };
}

window.addEventListener('error', (event) => {
  const errorMessage = event.error?.message || event.message || '';
  const errorName = event.error?.name || '';

  const isRadixPortalError =
    errorName === 'NotFoundError' && (
      (errorMessage.includes('removeChild') && errorMessage.includes('The node to be removed is not a child of this node')) ||
      (errorMessage.includes('insertBefore') && errorMessage.includes('The node before which the new node is to be inserted is not a child of this node'))
    );

  if (isRadixPortalError) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  if (looksLikeSubscriptionError(errorMessage)) {
    redirectForInactiveSubscription();
  }
});

/**
 * Detect "Subscription not active" / "Authentication required" thrown from API
 * responses. PostHog reports ~300/week — these are users without an active sub
 * hitting protected routes, not a real crash, so route them to the right page
 * instead of showing a stack trace.
 */
function looksLikeSubscriptionError(message: string): boolean {
  if (!message) return false;
  return (
    message.includes('Subscription not active') ||
    message.includes('Authentication required')
  );
}

/**
 * Pull current org lifecycle out of the React Query cache (no extra fetch).
 * Returns null if we don't have it cached yet.
 */
function readCachedLifecycle(): string | null {
  try {
    const sub: any = queryClient.getQueryData(['/api/billing/subscription']);
    const lifecycle =
      sub?.organization?.lifecycle ??
      sub?.lifecycle ??
      sub?.status ??
      null;
    return typeof lifecycle === 'string' ? lifecycle : null;
  } catch {
    return null;
  }
}

/**
 * Redirect to /reactivate (canceled orgs) or /select-plan (everyone else).
 * No-op if we're already on the destination page or on an auth/onboarding
 * surface that handles its own gating.
 */
function redirectForInactiveSubscription() {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '/';

  // Don't bounce auth / onboarding / billing / public marketing surfaces.
  const skipPrefixes = [
    '/auth/',
    '/activation',
    '/onboarding',
    '/select-plan',
    '/reactivate',
    '/checkout',
    '/post-checkout',
    '/plans',
    '/verify-email',
    '/mobile-login',
  ];
  if (skipPrefixes.some((p) => path.startsWith(p))) return;

  const lifecycle = readCachedLifecycle();
  const target = lifecycle === 'canceled' ? '/reactivate' : '/select-plan';
  if (path === target) return;
  window.location.assign(target);
}

window.addEventListener('unhandledrejection', (event) => {
  const reason: any = event.reason;
  const message: string =
    (typeof reason === 'string' && reason) ||
    reason?.message ||
    '';
  if (looksLikeSubscriptionError(message)) {
    event.preventDefault();
    redirectForInactiveSubscription();
  }
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || "",
  
  integrations: [
    Sentry.browserTracingIntegration(),
    
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: true,
    }),
  ],
  
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/.*\.limephone\.io/,
  ],
  
  replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0,
  replaysOnErrorSampleRate: 1.0,
  
  environment: import.meta.env.MODE || 'development',
  
  normalizeDepth: 5,
  maxBreadcrumbs: 50,
  
  ignoreErrors: [
    'Failed to load the JS script of the agent',
    'The object can not be found here',
    'removeChild',
    'insertBefore',
    'ResizeObserver loop',
    'ResizeObserver loop completed with undelivered notifications',
    'Non-Error promise rejection captured with value: undefined',
    'Failed to fetch dynamically imported module',
    'Load failed',
    'NetworkError',
    'AbortError',
    'The operation is insecure',
    'Access is denied for this document',
    'Blocked a frame with origin',
    'Failed to load Stripe',
    'Maximum call stack size exceeded',
    'Attempt to unregister when device is in state',
    'feature named',
    'Audio output selection is not supported',
    'Device not found: communications',
    'HTTP/1.1 Overhead',
    'Blocked by CSP',
    'runtime.sendMessage',
    'invalid origin',
    'is not a valid JavaScript MIME type',
    '.at is not a function', // Array.at() polyfill gaps in old browsers (web-vitals)
    'createOffer',
    'Cannot set property message of',
    'which has only a getter',
    '_connectionCallback',
    'Authentication required', // expected 401 from unauthenticated users hitting protected routes
    'No authentication token available', // expected when token is missing before redirect to login
    '401:', // prefix from queryClient throwIfResNotOk
    '@neondatabase/serverless', // legacy package — no longer used; filter any stale bundle references
    /^Loading chunk [\d]+ failed/,
    /^Loading CSS chunk [\d]+ failed/,
  ],
  
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    /googletagmanager\.com/i,
    /fpnpmcdn\.net/i,
  ],
  
  beforeSend(event, hint) {
    if (import.meta.env.PROD && event.user) {
      delete event.user.ip_address;
    }
    
    const errorMessage = event.exception?.values?.[0]?.value || '';
    const errorType = event.exception?.values?.[0]?.type || '';
    
    if (errorType === 'NotFoundError') {
      return null;
    }
    
    if (errorMessage.includes('Failed to load the JS script of the agent')) {
      return null;
    }
    
    if (errorType === 'SecurityError') {
      return null;
    }

    // "Subscription not active" / "Authentication required" are expected for
    // users hitting protected routes without an active sub. Redirect them and
    // drop the Sentry event (PostHog still gets it via autocapture for funnel
    // analysis). 300+ hits/week pre-fix.
    if (looksLikeSubscriptionError(errorMessage)) {
      redirectForInactiveSubscription();
      return null;
    }

    if (!event.fingerprint) {
      if (errorMessage.includes('Stripe')) {
        event.fingerprint = ['stripe', errorType, '{{ default }}'];
      } else if (errorMessage.includes('fetch') || errorMessage.includes('network')) {
        event.fingerprint = ['network-error', errorType];
      } else if (errorType === 'RangeError' && errorMessage.includes('Maximum call stack')) {
        event.fingerprint = ['max-call-stack', '{{ default }}'];
      }
    }

    // Best-effort mirror to /api/client-errors so we still capture when
    // Sentry / PostHog are blocked by ad-blockers or privacy extensions.
    // keepalive: true lets the request finish even if the page navigates away.
    try {
      const mirrorMessage = String(event.message ?? hint?.originalException ?? errorMessage ?? '').slice(0, 2000);
      const mirrorType = String(event.exception?.values?.[0]?.type ?? errorType ?? 'ClientError').slice(0, 200);
      const mirrorStack = String(
        event.exception?.values?.[0]?.stacktrace?.frames
          ?.map(f => `${f.filename}:${f.lineno}:${f.colno}`)
          .join('\n') ?? ''
      ).slice(0, 8000);
      fetch('/api/client-errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          message: mirrorMessage,
          errorType: mirrorType,
          stack: mirrorStack,
          url: typeof window !== 'undefined' ? window.location.href : '',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
      }).catch(() => { /* swallow */ });
    } catch { /* swallow */ }

    return event;
  },
});

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration);

        registration.addEventListener('updatefound', () => {
          const installingWorker = registration.installing;
          installingWorker?.addEventListener('statechange', () => {
            if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New LimePhone PWA version installed and ready on next reload');
            }
          });
        });
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}

createRoot(document.getElementById("root")!).render(<App />);
